import { CSSObject } from '@emotion/react'
import React, { useEffect, useState } from 'react'
import { Icon } from '@opallabs/kuiper'
import { usePrevious } from '@opallabs/lambda'
import {
  buttonStyles,
  collapsedStyles,
  countStyles,
  listChildDelay,
  listStyles,
  titleRowStyles,
} from './styles'

export interface CollapsibleListProps {
  defaultExpanded?: boolean
  iconName: string
  iconSystem?: 'gem' | 'hypernova'
  listMaxHeight?: number
  onExpand?: () => void
  styleOverrides?: {
    base: CSSObject
    titleRow?: CSSObject
    collapsed?: CSSObject
    list?: CSSObject
  }
  textRender: (count: number) => string
  title: string
  warning?: boolean
}

export const CollapsibleList: React.FC<
  React.PropsWithChildren<CollapsibleListProps>
> = ({
  children,
  warning = false,
  defaultExpanded = false,
  styleOverrides = {
    base: {},
    titleRow: {},
    collapsed: {},
    list: {},
  },
  title,
  iconName,
  iconSystem = 'gem',
  textRender,
  onExpand,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded)

  const previousChildrenCount = usePrevious<number>(
    React.Children.count(children)
  )

  const toggleExpanded = (): void => {
    if (React.Children.count(children) > 0) {
      setExpanded((currentExpanded) => !currentExpanded)
    }
  }

  useEffect(() => {
    if (expanded && onExpand) {
      onExpand()
    }
  }, [expanded, onExpand])

  useEffect(() => {
    if (React.Children.count(children) === 0) {
      setExpanded(false)
    }
    if (previousChildrenCount === 0 && React.Children.count(children) > 0) {
      setExpanded(true)
    }
  }, [children, previousChildrenCount])

  /* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
  return (
    <div css={styleOverrides.base}>
      <div
        css={[
          titleRowStyles(React.Children.count(children) === 0),
          styleOverrides.titleRow,
        ]}
        onClick={toggleExpanded}
      >
        <h4>{title}</h4>
        <button
          aria-label={expanded ? 'Collapse List' : 'Expand List'}
          css={buttonStyles}
        >
          <Icon
            ariaMode="hidden"
            glyph={expanded ? 'gem-chevron-s' : 'gem-chevron-e'}
            system="gem"
          />
        </button>
      </div>
      <div>
        {expanded ? (
          <ul css={[listStyles(), listChildDelay(), styleOverrides.list]}>
            {children}
          </ul>
        ) : (
          <div
            css={[
              collapsedStyles(warning, React.Children.count(children) === 0),
              styleOverrides.collapsed,
            ]}
            onClick={toggleExpanded}
          >
            <Icon
              glyph={iconName}
              system={iconSystem}
              ariaMode="presentational"
            />
            <span>{textRender(React.Children.count(children))}</span>
            <div css={countStyles(warning)}>
              <span css={{ marginBottom: '2px' }}>
                {React.Children.count(children)}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
  /* eslint-enable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
}
